import type { AppStore } from "@/store"
import { benefitApi } from "./benefitApi"
import { createBenefitReadRuntime } from "./benefitReadRuntime"
import { createBenefitSubscriptionRegistry } from "@/services/realtime/benefitSubscriptions"
import { browserBenefitRefreshEnvironment, type BenefitRefreshEnvironment } from "@/services/realtime/benefitBackgroundRefresh"
import type { RefreshClock } from "@/services/realtime/resourceRefreshCoordinator"
import { requestStaffAccessRecheck } from "@/services/staffTransportSession"

const runtimes = new WeakMap<AppStore, ReturnType<typeof createBenefitReadRuntime>>()
export function benefitReadRuntime(store: AppStore) {
  let runtime = runtimes.get(store)
  if (!runtime) { runtime = createBenefitReadRuntime(store, benefitApi); runtimes.set(store, runtime) }
  return runtime
}

/** Install before mounting readers. Uses the existing session transport, never a second socket.
 * Explicit opt-in for the integrated harness; live activation requires the V1 readiness decision. */
export function enableBenefitBackground(store: AppStore, transport: Parameters<typeof createBenefitSubscriptionRegistry>[0], options: {
  environment?: BenefitRefreshEnvironment; clock?: RefreshClock; random?: () => number
} = {}) {
  if (runtimes.has(store)) throw new Error("Configure benefit refresh before mounting readers.")
  const subscriptions = createBenefitSubscriptionRegistry(transport, { clock: options.clock })
  const runtime = createBenefitReadRuntime(store, benefitApi, {
    subscriptions, clock: options.clock, scopeChanged: requestStaffAccessRecheck,
    background: { environment: options.environment ?? browserBenefitRefreshEnvironment, random: options.random },
  })
  runtimes.set(store, runtime)
  return {
    runtime, subscriptions,
    dispose() {
      runtime.dispose(); subscriptions.dispose()
      if (runtimes.get(store) === runtime) runtimes.delete(store)
    },
  }
}
